import { useState, type ReactNode } from 'react'

export interface AccordionItem {
  title: string
  body: ReactNode
}

export interface AccordionProps {
  items: AccordionItem[]
  /** Index open on first render; the first item is open by default. */
  defaultOpen?: number | null
  className?: string
}

/**
 * Single-open accordion. Used on the FAQ and Services pages, which share identical
 * markup, so one component covers both.
 *
 * Single-open: clicking the open item closes it, and opening one closes whichever was
 * open. `.open` on `.ul-single-accordion-item` is the whole contract — the CSS animates
 * the row via `grid-template-rows`, so the class name and the header/body element
 * structure must not change.
 */
export function Accordion({ items, defaultOpen = 0, className }: AccordionProps) {
  const [open, setOpen] = useState<number | null>(defaultOpen)

  return (
    <div className={className ? `ul-accordion ${className}` : 'ul-accordion'}>
      {items.map((item, i) => (
        <div
          key={item.title}
          className={`ul-single-accordion-item${open === i ? ' open' : ''}`}
          onClick={() => setOpen((cur) => (cur === i ? null : i))}
        >
          <div className="ul-single-accordion-item__header">
            <div className="left">
              <h3 className="ul-single-accordion-item__title">{item.title}</h3>
            </div>
            <span className="icon">
              <i className="flaticon-next" />
            </span>
          </div>

          <div className="ul-single-accordion-item__body">{item.body}</div>
        </div>
      ))}
    </div>
  )
}
